import React, { useState, useEffect, useMemo } from "react";
import {
  Icon,
  Button,
  Input,
  Message,
  Confirm,
} from "semantic-ui-react";
import styled from "styled-components";

import Select from "react-select";

import ServicesEnabled from "data/ServicesEnabled.json";

import {
  SURVEY_SEQUENCE_LIST,
  ORG_CATEGORY,
  QUESTION_ASK_AGAIN,
} from "data/AdminData";

import { Switch } from "components/ui/switch";
import { Button as UiButton } from "components/ui/button";
import { Input as UiInput } from "components/ui/input";
import { Separator as UiSeparator } from "components/ui/separator";
import { Trash2, PlusCircle, Settings2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import { Label } from "components/ui/label";

const StatusList = [
  { value: "min", label: "Min" },
  { value: "max", label: "Max" },
];

// Standard keys that can be managed through the modal
const STANDARD_STYLING_KEYS = [
  {
    key: "Rule",
    label: "Anonymity Threshold Rule",
    description: "Sets the anonymity threshold on the platform",
    defaultValue: "5",
    type: "number",
    placeholder: "e.g. 5"
  },
  {
    key: "organization",
    label: "Organization Setting",
    description: "Removes duplicate primary after the category questions",
    defaultValue: "2",
    type: "number",
    placeholder: "e.g. 2"
  },
  {
    key: "survey_qualifer",
    label: "Survey Qualifier",
    description: "Customizes the qualifier before the survey question is asked, stock option = \"In this culture…\"",
    defaultValue: "In this culture…",
    type: "text",
    placeholder: "e.g. In this culture…"
  },
  {
    key: "survey_qualifier_fr",
    label: "Survey Qualifier (French)",
    description: "French version of the survey qualifier",
    defaultValue: "Dans cette culture…",
    type: "text",
    placeholder: "e.g. Dans cette culture…"
  },
  {
    key: "custom_survey_header",
    label: "Custom Survey Header",
    description: "Customizes the head of the survey, stock option = \"Culture Question\"",
    defaultValue: "Culture Question",
    type: "text",
    placeholder: "e.g. Culture Question"
  },
  {
    key: "custom_survey_header_fr",
    label: "Custom Survey Header (French)",
    description: "French version of the custom survey header",
    defaultValue: "Question culturelle",
    type: "text",
    placeholder: "e.g. Question culturelle"
  },
  {
    key: "custom_survey_header_color",
    label: "Custom Survey Header Color",
    description: "Hex color for the survey header",
    defaultValue: "#6a00ff",
    type: "color",
    placeholder: "#6a00ff"
  },
  {
    key: "remove_survey_qualifer",
    label: "Remove Survey Qualifier",
    description: "Removes the qualifier (set to 'true' to enable)",
    defaultValue: "false",
    type: "text",
    placeholder: "true or false"
  },
  {
    key: "team_qualifier",
    label: "Team Qualifier",
    description: "Adds a qualifier that injects the team name (use {team} as placeholder)",
    defaultValue: "In {team}'s culture...",
    type: "text",
    placeholder: "e.g. In {team}'s culture..."
  },
  {
    key: "employee_id_domain",
    label: "Employee ID Domain",
    description: "Sets the domain for employee ID generation and validation",
    defaultValue: "",
    type: "text",
    placeholder: "e.g. company.com"
  },
];

export function RowCheckList(services) {
  const ServiceList = ["Teamtool", "EDI", "Audit", "OTP"];
  return (
    services &&
    services.map((sr, i) => {
      if (ServiceList.includes(sr?.name)) {
        if (sr?.enabled === true) {
          return (
            <div key={i}>
              <Icon color="green" name="checkmark" size="small" />
              <SectionHeader>{sr.name}</SectionHeader>

              <SectionHeader>
                {sr.question_sort_order > 0
                  ? `Specific Question ${sr.question_sort_order}`
                  : "Generic Question"}
              </SectionHeader>
            </div>
          );
        } else {
          return (
            <div key={i}>
              <Icon color="red" name="close" size="small" />
              <SectionHeader>{sr.name}</SectionHeader>

              <SectionHeader>
                {sr.question_sort_order > 0
                  ? `Specific Question ${sr.question_sort_order}`
                  : "Generic Question"}
              </SectionHeader>
            </div>
          );
        }
      }
      return null;
    })
  );
}

export function RowObjectList(services) {
  const [objectList, setObjectList] = useState([]);
  useEffect(() => {
    // setObjectList(Object.entries(services).filter(i => i[0] !== 'rules'))
    setObjectList(
      Object.entries(services).map((item, i) => {
        if (typeof item[1] !== "object") {
          return item;
        }
        return null;
      })
    );
  }, [services]);

  return (
    <>
      <SectionHeader>Styling</SectionHeader>
      {objectList.map((item, i) => {
        if (item) {
          return (
            <div key={i}>
              <RowData>
                <RowLine> {item[0]}</RowLine>
                <RowLine> {item[1]}</RowLine>
              </RowData>
            </div>
          );
        }
        return null;
      })}
    </>
  );
}

export function RowObjectInputEdit({
  stylingList,
  setStylingList,
  setUpdateValue,
}) {
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [error, setError] = useState("");
  const [isStandardKeysModalOpen, setIsStandardKeysModalOpen] = useState(false);
  const [standardKeyValues, setStandardKeyValues] = useState({});

  const entries = useMemo(() => {
    if (!stylingList) return [];
    return Object.entries(stylingList).filter(([, value]) => typeof value !== "object");
  }, [stylingList]);

  // Initialize standard key values from existing styling
  useEffect(() => {
    const values = {};
    STANDARD_STYLING_KEYS.forEach(standardKey => {
      if (stylingList && Object.prototype.hasOwnProperty.call(stylingList, standardKey.key)) {
        values[standardKey.key] = stylingList[standardKey.key];
      }
    });
    setStandardKeyValues(values);
  }, [stylingList]);

  function handleDelete(key) {
    if (!key) return;
    const updated = { ...(stylingList || {}) };
    delete updated[key];
    setStylingList(updated);
    setUpdateValue(true);
  }

  function handleAdd() {
    const trimmedKey = newKey.trim();
    const trimmedValue = newValue.trim();

    if (!trimmedKey || !trimmedValue) {
      setError("Please provide both a key and value.");
      return;
    }

    if (stylingList && Object.prototype.hasOwnProperty.call(stylingList, trimmedKey)) {
      setError("That key already exists. Choose a different name.");
      return;
    }

    const updated = { ...(stylingList || {}), [trimmedKey]: trimmedValue };
    setStylingList(updated);
    setUpdateValue(true);
    setNewKey("");
    setNewValue("");
    setError("");
  }

  function handleToggleStandardKey(standardKey, enabled) {
    const updated = { ...(stylingList || {}) };
    
    if (enabled) {
      // Add the standard key with its value or default value
      const value = standardKeyValues[standardKey.key] || standardKey.defaultValue;
      updated[standardKey.key] = value;
      setStandardKeyValues(prev => ({ ...prev, [standardKey.key]: value }));
    } else {
      // Remove the standard key
      delete updated[standardKey.key];
      const newValues = { ...standardKeyValues };
      delete newValues[standardKey.key];
      setStandardKeyValues(newValues);
    }
    
    setStylingList(updated);
    setUpdateValue(true);
  }

  function handleStandardKeyValueChange(key, value) {
    setStandardKeyValues(prev => ({ ...prev, [key]: value }));
    const updated = { ...(stylingList || {}), [key]: value };
    setStylingList(updated);
    setUpdateValue(true);
  }

  function isStandardKeyEnabled(key) {
    return stylingList && Object.prototype.hasOwnProperty.call(stylingList, key);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-muted/30">
        <div className="flex items-center justify-between border-b border-border px-4 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Styling Overrides
          </span>
          <div className="flex items-center gap-2">
            <Dialog open={isStandardKeysModalOpen} onOpenChange={setIsStandardKeysModalOpen}>
              <DialogTrigger asChild>
                <UiButton variant="outline" size="sm">
                  <Settings2 className="mr-2 h-4 w-4" />
                  Manage Standard Keys
                </UiButton>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Manage Standard Styling Keys</DialogTitle>
                  <DialogDescription>
                    Enable or disable standard styling keys for this organization. These are predefined keys with specific purposes.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {STANDARD_STYLING_KEYS.map((standardKey) => {
                    const enabled = isStandardKeyEnabled(standardKey.key);
                    return (
                      <div key={standardKey.key} className="rounded-lg border border-border p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={enabled}
                                onCheckedChange={(checked) => handleToggleStandardKey(standardKey, checked)}
                                aria-label={`Toggle ${standardKey.label}`}
                              />
                              <div>
                                <Label className="text-sm font-semibold">{standardKey.label}</Label>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {standardKey.description}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Key: <code className="text-xs bg-muted px-1 py-0.5 rounded">{standardKey.key}</code>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        {enabled && (
                          <div className="ml-11">
                            <Label className="text-xs font-medium uppercase text-muted-foreground mb-1 block">
                              Value
                            </Label>
                            {standardKey.type === "color" ? (
                              <div className="flex items-center gap-3">
                                <input
                                  type="color"
                                  className="h-9 w-10 p-0 border border-input rounded-md bg-background"
                                  value={standardKeyValues[standardKey.key] || "#000000"}
                                  onChange={(e) => handleStandardKeyValueChange(standardKey.key, e.target.value)}
                                  aria-label={`${standardKey.label} color picker`}
                                />
                                <UiInput
                                  type="text"
                                  className="max-w-[160px]"
                                  value={standardKeyValues[standardKey.key] || ""}
                                  onChange={(e) => handleStandardKeyValueChange(standardKey.key, e.target.value)}
                                  placeholder={standardKey.placeholder || "#000000"}
                                  aria-label={`${standardKey.label} hex value`}
                                />
                              </div>
                            ) : (
                              <UiInput
                                type={standardKey.type || "text"}
                                value={standardKeyValues[standardKey.key] || ""}
                                onChange={(e) => handleStandardKeyValueChange(standardKey.key, e.target.value)}
                                placeholder={standardKey.placeholder}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {STANDARD_STYLING_KEYS.length === 0 && (
                    <div className="text-center text-muted-foreground py-8">
                      No standard keys configured yet. Add them to the STANDARD_STYLING_KEYS array.
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <span className="text-xs text-muted-foreground">
              {entries.length} {entries.length === 1 ? "entry" : "entries"}
            </span>
          </div>
        </div>

        <div className="divide-y divide-border text-sm">
          {entries.length === 0 && (
            <div className="px-4 py-6 text-center text-muted-foreground">
              No styling overrides defined yet.
            </div>
          )}

          {entries.map(([key, value]) => {
            const isStandard = STANDARD_STYLING_KEYS.some(sk => sk.key === key);
            return (
              <div key={key} className="flex flex-wrap items-center gap-4 px-4 py-3">
                <div className="min-w-[180px] font-medium text-foreground">
                  {key}
                  {isStandard && (
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(Standard)</span>
                  )}
                </div>
                <UiSeparator orientation="vertical" className="hidden h-6 sm:block" />
                <div className="min-w-[200px] flex-1 text-muted-foreground break-words">
                  {String(value)}
                </div>
                <UiButton
                  variant="ghost"
                  size="icon"
                  className="ml-auto text-muted-foreground hover:text-destructive"
                  onClick={() => handleDelete(key)}
                  aria-label={`Delete ${key}`}
                >
                  <Trash2 className="h-4 w-4" />
                </UiButton>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-border bg-background px-4 py-4">
        <div className="mb-3 text-sm font-semibold">Add Custom Styling Entry</div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
              Key
            </label>
            <UiInput
              value={newKey}
              onChange={(e) => {
                setNewKey(e.target.value);
                if (error) setError("");
              }}
              placeholder="e.g. primary_color"
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
              Value
            </label>
            <UiInput
            value={newValue}
            onChange={(e) => {
              setNewValue(e.target.value);
                if (error) setError("");
              }}
              placeholder="e.g. #123456"
            />
          </div>
          <div className="flex items-end">
            <UiButton onClick={handleAdd} className="w-full sm:w-auto">
              <PlusCircle className="mr-2 h-4 w-4" />
              Add
            </UiButton>
          </div>
        </div>
        {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      </div>
    </div>
  );
}

export function RulesSubList({ stylingList, setStylingList, setUpdateValue }) {
  const [newRuleType, setNewRuleType] = useState(null)
  const [newRuleValue, setNewRuleValue] = useState("")
  const [error, setError] = useState("")

  const rules = Array.isArray(stylingList?.rules) ? stylingList.rules : []

  const handleAddRulesContainer = () => {
    setStylingList({ ...stylingList, rules: [] })
    setUpdateValue(true)
  }

  const handleDeleteRule = (rule) => {
    setStylingList({
      ...stylingList,
      rules: rules.filter((r) => r.type !== rule.type),
    })
    setUpdateValue(true)
  }

  const handleAddRule = () => {
    if (!newRuleType || !newRuleValue.trim()) {
      setError("Please choose a rule type and provide a value.")
      return
    }

    if (rules.some((rule) => rule.type === newRuleType.value)) {
      setError("That rule already exists.")
      return
    }

    const updated = [
      ...rules,
      { type: newRuleType.value, rows: newRuleValue.trim() },
    ]
    setStylingList({ ...stylingList, rules: updated })
    setUpdateValue(true)
    setNewRuleType(null)
    setNewRuleValue("")
    setError("")
  }

  if (!Array.isArray(stylingList?.rules)) {
    return (
      <UiButton onClick={handleAddRulesContainer} variant="secondary">
        Enable Rules
      </UiButton>
    )
  }

        return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full border-collapse">
          <thead className="bg-muted/40 text-xs font-semibold uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-left">Value</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-sm">
            {rules.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
                  No rules configured.
                </td>
              </tr>
            )}

            {rules.map((rule) => (
              <tr key={rule.type} className="bg-background">
                <td className="px-4 py-3 align-top font-medium text-foreground">
                  {rule.type}
                </td>
                <td className="px-4 py-3 align-top text-muted-foreground">
                  {rule.rows}
                </td>
                <td className="px-4 py-3 text-right align-top">
                  <UiButton
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteRule(rule)}
                    aria-label={`Delete ${rule.type}`}
                    className="ml-auto text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </UiButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-dashed border-border bg-background px-4 py-4">
        <div className="mb-3 text-sm font-semibold">Add Rule</div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
              Rule Type
            </label>
          <Select
              placeholder="Select type"
              value={newRuleType}
              onChange={(option) => {
                setNewRuleType(option)
                if (error) setError("")
              }}
            options={StatusList}
              classNamePrefix="rules-select"
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium uppercase text-muted-foreground">
              Value
            </label>
            <UiInput
              value={newRuleValue}
            onChange={(e) => {
                setNewRuleValue(e.target.value)
                if (error) setError("")
              }}
              placeholder="Describe when this rule triggers"
            />
          </div>
          <div className="flex items-end">
            <UiButton onClick={handleAddRule} className="w-full sm:w-auto">
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Rule
            </UiButton>
          </div>
        </div>
        {error && <div className="mt-3 text-xs text-destructive">{error}</div>}
      </div>
    </div>
  )
}

export function SurveySequenceList({
  stylingList,
  setStylingList,
  setUpdateValue,
  get_survey_question_all_select,
}) {
  const [questionSelect, setQuestionSelect] = useState([])
  const [confirmDel, setConfirmDel] = useState(false)

  useEffect(() => {
    const options = get_survey_question_all_select.map((sr) => ({
        value: sr.sort_order,
        label: `${sr.name} (sort order ${sr.sort_order})`,
        survey_type: sr.survey_type,
        sort_order: sr.sort_order,
    }))
    setQuestionSelect(options)
  }, [get_survey_question_all_select])

  const handleAddDefaults = () => {
    setStylingList({ ...stylingList, survey_sequence: SURVEY_SEQUENCE_LIST })
    setUpdateValue(true)
  }

  const handleAddMissingRow = (row) => {
    setStylingList((prev) => ({
      ...prev,
      survey_sequence: [...(prev?.survey_sequence || []), row].sort(
        (a, b) => a.sort_order - b.sort_order
      ),
    }))
    setUpdateValue(true)
  }

  const handleToggle = (row) => {
    setStylingList((prev) => ({
      ...prev,
      survey_sequence: prev.survey_sequence
        .map((seq) =>
          seq.id === row.id ? { ...seq, enabled: !seq.enabled } : seq
        )
        .sort((a, b) => a.sort_order - b.sort_order),
    }))
    setUpdateValue(true)
  }

  const handleQuestionChange = (value, row) => {
    setStylingList((prev) => ({
      ...prev,
      survey_sequence: prev.survey_sequence
        .map((seq) => (seq.id === row.id ? { ...seq, question: value } : seq))
        .sort((a, b) => a.sort_order - b.sort_order),
    }))
    setUpdateValue(true)
  }

  const handleAskAgainChange = (value, row) => {
    setStylingList((prev) => ({
      ...prev,
      survey_sequence: prev.survey_sequence
        .map((seq) => (seq.id === row.id ? { ...seq, ask_again: value } : seq))
        .sort((a, b) => a.sort_order - b.sort_order),
    }))
    setUpdateValue(true)
  }

  const handleRemoveRow = (row) => {
    setStylingList((prev) => ({
      ...prev,
      survey_sequence: prev.survey_sequence.filter((seq) => seq.id !== row.id),
    }))
    setUpdateValue(true)
  }

  const handleRemoveAll = () => {
    setStylingList({ ...stylingList, survey_sequence: [] })
    setUpdateValue(true)
    setConfirmDel(false)
  }

  const sequence = stylingList?.survey_sequence || []
  const missingDefaults = SURVEY_SEQUENCE_LIST.filter(
    (item) => !sequence.some((seq) => seq.id === item.id)
  )

  if (sequence.length === 0) {
    return (
      <UiButton onClick={handleAddDefaults} variant="secondary">
        Add Survey Sequence
      </UiButton>
    )
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full border-collapse">
          <thead className="bg-muted/40 text-xs font-semibold uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Stage</th>
              <th className="px-3 py-2 text-left">Order</th>
              <th className="px-3 py-2 text-left">Enabled</th>
              <th className="px-3 py-2 text-left">Question</th>
              <th className="px-3 py-2 text-left">Ask Again</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-sm">
            {sequence
              .slice()
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((row) => {
                const questionOptions = questionSelect.filter(
                  (q) => q.survey_type === row.survey_type
                )
 
                 return (
                   <tr key={row.id} className="bg-background">
                     <td className="px-3 py-3 align-top">
                       <div className="font-medium text-foreground">{row.label}</div>
                       <div className="text-xs text-muted-foreground">{row.description}</div>
                     </td>
                     <td className="px-3 py-3 align-top">
                       <UiInput value={row.sort_order} disabled className="max-w-[80px] text-center" />
                     </td>
                     <td className="px-3 py-3 align-top">
                       {row.editable ? (
                         <div className="flex items-center gap-2">
                           <Switch
                             checked={Boolean(row.enabled)}
                             onCheckedChange={() => handleToggle(row)}
                             aria-label={`Toggle ${row.label}`}
                           />
                           <span className="text-xs text-muted-foreground">
                             {row.enabled ? "Active" : "Inactive"}
                           </span>
                         </div>
                       ) : (
                         <span className="text-xs text-muted-foreground">Always On</span>
                       )}
                     </td>
                     <td className="px-3 py-3 align-top">
                       {row.editable ? (
                         <div className="min-w-[220px] max-w-[320px]">
                           <Select
                             classNamePrefix="sequence-select"
                             name="survey"
                             options={questionOptions}
                             onChange={(value) => handleQuestionChange(value, row)}
                             value={row.question || null}
                             menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                             styles={{
                               menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                             }}
                           />
                         </div>
                       ) : (
                         <div className="text-xs text-muted-foreground">{row.description}</div>
                       )}
                     </td>
                     <td className="px-3 py-3 align-top">
                       {row.editable ? (
                         <div className="min-w-[240px] max-w-[360px]">
                           <Select
                             classNamePrefix="sequence-select"
                             name="ask_again"
                             options={QUESTION_ASK_AGAIN}
                             onChange={(value) => handleAskAgainChange(value, row)}
                             value={row.ask_again || null}
                             menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                             styles={{
                               menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                             }}
                           />
                         </div>
                       ) : row.ask_again ? (
                         <div className="text-xs text-muted-foreground">{row.ask_again.description}</div>
                       ) : (
                         <div className="text-xs text-muted-foreground">—</div>
                       )}
                     </td>
                     <td className="px-3 py-3 text-right align-top">
                       {row.editable && (
                         <UiButton
                           variant="ghost"
                           size="icon"
                           onClick={() => handleRemoveRow(row)}
                           aria-label={`Remove ${row.label}`}
                           className="ml-auto text-muted-foreground hover:text-destructive"
                         >
                           <Trash2 className="h-4 w-4" />
                         </UiButton>
                       )}
                     </td>
                   </tr>
                 )
               })}
          </tbody>
        </table>
      </div>

      {missingDefaults.length > 0 && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-4">
          <div className="mb-3 text-sm font-semibold text-muted-foreground">Available Stages</div>
          <div className="space-y-2">
            {missingDefaults.map((row) => (
              <div
                key={row.id}
                className="flex flex-wrap items-center gap-3 rounded border border-border bg-background px-3 py-2"
              >
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium text-foreground">{row.label}</div>
                  <div className="text-xs text-muted-foreground">{row.description}</div>
                </div>
                <UiButton size="sm" variant="secondary" onClick={() => handleAddMissingRow(row)}>
                  Add Stage
                </UiButton>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <UiButton variant="outline" size="sm" onClick={() => setConfirmDel(true)}>
          Remove All Stages
        </UiButton>
        <UiButton variant="secondary" size="sm" onClick={handleAddDefaults}>
          Reset to Defaults
        </UiButton>
      </div>

      <Confirm
        open={confirmDel}
        content="This will remove the current survey sequence. Continue?"
        onCancel={() => setConfirmDel(false)}
        onConfirm={handleRemoveAll}
      />
    </div>
  )
}




export function RowEditList({
  serviceList,
  setServiceList,
  setUpdateValue,
  get_survey_question_select,
}) {
  const ServiceListing = [
    "Teamtool",
    "EDI",
    "Audit",
    "OTP",
    "Prototype",
    "In Survey Personality",
  ];

  function SelectQuestionList({ sr, sl }) {
    const [questionSelected, setQuestionSelected] = useState([]);
    const [questionSelect, setQuestionSelect] = useState([]);

    useEffect(() => {
      const _SelectList = [{ value: 0, label: "Default Question" }];
      get_survey_question_select
        .filter((sqs) => sqs.survey_type_text === sl)
        .map((sr) => {
          _SelectList.push({
            value: sr.sort_order,
            label: `${sr.name} (sort order ${sr.sort_order})`,
          });
          return null;
        });
      setQuestionSelect(_SelectList);
    }, [sl, get_survey_question_select]);

    useEffect(() => {
      setQuestionSelected(
        questionSelect.filter((slt) => slt.value === sr.question_sort_order)
      );
    }, [questionSelect, sr]);

    function ChangedQuestionSort(option) {
      setQuestionSelected(option);
      setServiceList((prev) =>
        prev.map((service) =>
          service.name === sr.name
            ? { ...service, question_sort_order: option?.value ?? 0 }
            : service
        )
      );
      setUpdateValue(true);
    }

    return (
      <Select
        classNamePrefix="services-select"
        name="survey_preference"
        options={questionSelect}
        onChange={ChangedQuestionSort}
        value={questionSelected}
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        styles={{
          container: (base) => ({ ...base, width: "100%" }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
      />
    );
  }

  function toggleService(sr) {
    setServiceList((prev) =>
      prev
        .map((service) =>
          service.name === sr.name
            ? { ...service, enabled: !service.enabled }
            : service
        )
        .sort((a, b) => a.id - b.id)
    );
    setUpdateValue(true);
  }

  function addService(name) {
    const serviceDefinition = ServicesEnabled.find((service) => service.name === name);
    if (!serviceDefinition) return;

    setServiceList((prev) => {
      if (prev.some((service) => service.name === name)) {
        return prev;
      }
      return [...prev, serviceDefinition].sort((a, b) => a.id - b.id);
    });
    setUpdateValue(true);
  }

  if (!serviceList) {
    return null;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="min-w-[640px] w-full table-fixed border-collapse">
        <thead className="bg-muted/50">
          <tr className="text-left text-xs font-semibold uppercase text-muted-foreground">
            <th className="px-4 py-2">Service</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Question Preference</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border text-sm">
          {ServiceListing.map((name) => {
            const service = serviceList.find((sr) => sr?.name === name);

            if (!service) {
              return (
                <tr key={name} className="bg-muted/10">
                  <td className="px-4 py-3 font-medium text-foreground">{name}</td>
                  <td className="px-4 py-3 text-muted-foreground">Not configured</td>
                  <td className="px-4 py-3">
                    <Button size="tiny" onClick={() => addService(name)}>
                      Add Service
                    </Button>
                  </td>
                </tr>
              );
            }

      return (
              <tr key={service.id} className="bg-background">
                <td className="px-4 py-3 align-middle">
                  <div className="font-medium text-foreground">{service.name}</div>
                  <div className="text-xs text-muted-foreground">
                    Configure how this appears to respondents.
                  </div>
                </td>
                <td className="px-4 py-3 align-middle">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={service.enabled}
                      onCheckedChange={() => toggleService(service)}
                      aria-label={`Toggle ${service.name}`}
                    />
                    <span className="text-xs font-medium text-muted-foreground">
                      {service.enabled ? "Active" : "Inactive"}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 align-middle">
                  <SelectQuestionList sr={service} sl={name} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function OrgCategoryObjectInputEdit({
  stylingList,
  setStylingList,
  setUpdateValue,
}) {
  const [newValue, setNewValue] = useState("");

  function DeleteRow(e) {
    let _newList = stylingList;
    delete _newList[e];
    setStylingList(_newList);
    setUpdateValue(`${e} + ${true}`);
  }

  function AddRow(row) {
    let _newList = stylingList;
    _newList[row.value] = row.defaultValue;
    setStylingList(_newList);
    setUpdateValue(`${Math.random()} + ${true}`);
  }

  function ChangeSelection(sr, value) {
    let _newList = stylingList;
    _newList[sr?.value] = value;
    setStylingList(_newList);
    setUpdateValue(`${sr} + ${true}`);
    setNewValue(Math.random);
  }

  return (
    <>
      {ORG_CATEGORY.map((orc, i) => {
        if (stylingList?.[orc?.value] !== undefined) {
          return (
            <div key={i}>
              <RowData>
                <RowInputLine>{orc?.label}</RowInputLine>

                <RowInputLine>
                  {orc?.type === "input" && (
                    <Input
                      value={stylingList[orc?.value]}
                      onChange={(e) => ChangeSelection(orc, e.target.value)}
                    />
                  )}

                  {orc?.type === "toggle" && (
                    <Switch
                      checked={stylingList[orc?.value] === true}
                      onCheckedChange={(checked) =>
                        ChangeSelection(orc, Boolean(checked))
                      }
                      aria-label={`Toggle ${orc?.label}`}
                    />
                  )}
                </RowInputLine>
                <RowInputLine>
                  <Icon
                    style={{
                      cursor: "pointer",
                      marginTop: "5px",
                    }}
                    onClick={() => DeleteRow(orc?.value)}
                    name="trash alternate"
                  />
                </RowInputLine>
              </RowData>
            </div>
          );
        }

        return (
          <SectionDiv key={i}>
            <Button
              onClick={() => AddRow(orc)}
            >{`Not Found Click to Add ${orc?.label}`}</Button>
          </SectionDiv>
        );
      })}
    </>
  );
}

const SectionDiv = styled.div`
  padding: 5px;
  margin: 5px;
`;

const SectionHeader = styled.span`
  min-width: 300px;
  font-size: 110%;
  margin: 0 10px;
  display: inline-block;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RowInputLine = styled.span`
  min-width: 300px;
  font-size: 110%;
  margin: 10px;
  display: inline-block;
`;

const SmallRowInputLine = styled.span`
  width: ${(props) => (props?.width ? `${props.width}px` : "100px")};
  // min-width: 100px;
  font-size: 110%;
  margin: 10px;
  display: inline-block;
`;

const RowLine = styled.span`
  max-width: 300px;
  font-size: 110%;
  margin: 0 10px;
  display: inline-block;
`;

const RowData = styled.div`
  display: flex;
  flex-direction: row;
  border: ${(props) => (props.showBorder ? "solid 0.1pt grey" : "none")};
  // border-color: red;
  background-color: ${(props) => (props.showBorder ? "#dcdcdc" : "")};
`;
